﻿import { ReactElement, useMemo, useState, useCallback, useRef } from "react";
import { gpagedimensions, gautoparagraph, gautoparagraphresult } from "../../interfaces";
import { pdfSourceCodes } from "../../lsts";
import { toast } from "react-toastify";
import styles from "./ParagraphOverlay.module.css";
import { captureCanvasCrop, RectCropData } from "../XplrrBOXPDF/pdfCropUtil";

interface ParagraphOverlayProps {
    paragraphs: gautoparagraphresult[];
    pagedimensions: gpagedimensions[];
    pageNumber: number;
    pageWidth: number;
    pageHeight: number;
    visible: boolean;
    onParagraphClick?: (paragraph: gautoparagraph) => void;
    onRectCrop?: (cropData: RectCropData) => void;
}

interface SelectionRect {
    startX: number;
    startY: number;
    currentX: number;
    currentY: number;
}

interface ParagraphBounds {
    kgnf_id: number;
    left: number;
    top: number;
    right: number;
    bottom: number;
    content: string;
}

const INCHES_TO_POINTS = 72;

const rectIntersects = (
    r1: { left: number; top: number; right: number; bottom: number },
    r2: { left: number; top: number; right: number; bottom: number }
): boolean => {
    return !(r2.left > r1.right || r2.right < r1.left || r2.top > r1.bottom || r2.bottom < r1.top);
};

export const ParagraphOverlay = ({
    paragraphs,
    pagedimensions,
    pageNumber,
    pageWidth,
    pageHeight,
    visible,
    onParagraphClick,
    onRectCrop
}: ParagraphOverlayProps): ReactElement | null => {
    const [clickedId, setClickedId] = useState<number | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [selection, setSelection] = useState<SelectionRect | null>(null);
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const containerRef = useRef<HTMLDivElement>(null);
    const paragraphBoundsRef = useRef<ParagraphBounds[]>([]);

    const pageParagraphs = useMemo(() => {
        if (!paragraphs || paragraphs?.[0]?.paragrphs?.length < 1) return [];

        return paragraphs?.[0]?.paragrphs?.filter(
            para => para.bounding_regions?.[0]?.page_number === pageNumber + 1
        );
    }, [paragraphs, pageNumber]);

    const xScale = useMemo(() => {
        if (!pagedimensions?.[pageNumber]?.width) return 1;
        return pageWidth / pagedimensions[pageNumber].width;
    }, [pageWidth, pagedimensions, pageNumber]);

    const yScale = useMemo(() => {
        if (!pagedimensions?.[pageNumber]?.height) return 1;
        return pageHeight / pagedimensions[pageNumber].height;
    }, [pageHeight, pagedimensions, pageNumber]);

    // Calculate bounds for all paragraphs
    const paragraphBounds = useMemo(() => {
        import.meta.env.DEV && console.log('‹ppagphs›', pageParagraphs || []);
        const bounds: ParagraphBounds[] = pageParagraphs?.map(paragraph => {
            const poly = paragraph.bounding_regions?.[0]?.polygon || [];
            if (poly.length === 0) return null;

            const pixelCoordinates = poly.map(point => ({
                x: point.x * INCHES_TO_POINTS * xScale,
                y: point.y * INCHES_TO_POINTS * yScale
            }));

            const xValues = pixelCoordinates.map(p => p.x);
            const yValues = pixelCoordinates.map(p => p.y);

            return {
                kgnf_id: paragraph.kgnf_id,
                left: Math.min(...xValues),
                right: Math.max(...xValues),
                top: Math.min(...yValues),
                bottom: Math.max(...yValues),
                content: paragraph.content || ""
            };
        })?.filter(Boolean) as ParagraphBounds[];

        paragraphBoundsRef.current = bounds;
        return bounds;
    }, [pageParagraphs, xScale, yScale]);

    const getSelectionRect = useCallback(() => {
        if (!selection) return null;
        const left = Math.min(selection.startX, selection.currentX);
        const top = Math.min(selection.startY, selection.currentY);
        const right = Math.max(selection.startX, selection.currentX);
        const bottom = Math.max(selection.startY, selection.currentY);
        return { left, top, right, bottom };
    }, [selection]);

    const findIntersectingParagraphs = useCallback((selRect: { left: number; top: number; right: number; bottom: number }) => {
        const intersecting = new Set<number>();
        for (const bounds of paragraphBoundsRef.current) {
            if (rectIntersects(selRect, bounds)) {
                intersecting.add(bounds.kgnf_id);
            }
        }
        return intersecting;
    }, []);

    const copySelectedToClipboard = useCallback(async (ids: Set<number>) => {
        if (ids.size === 0) return;

        const selectedContent = paragraphBoundsRef.current
            .filter(b => ids.has(b.kgnf_id))
            .sort((a, b) => a.top - b.top) // sort by vertical position
            .map(b => b.content)
            .join("\n");

        if (selectedContent) {
            try {
                await navigator.clipboard.writeText(selectedContent);
                toast.success("Copied to clipboard", { autoClose: 500 });
            } catch (err) {
                console.error("Failed to copy to clipboard:", err);
            }
        }
    }, []);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (e.button !== 0) return; // Only left click
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;

        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        setIsDragging(true);
        setSelection({ startX: x, startY: y, currentX: x, currentY: y });
        setSelectedIds(new Set());
    }, []);

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        if (!isDragging || !selection) return;

        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;

        const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
        const y = Math.max(0, Math.min(e.clientY - rect.top, rect.height));

        setSelection(prev => prev ? { ...prev, currentX: x, currentY: y } : null);

        // Update selected paragraphs
        const selRect = {
            left: Math.min(selection.startX, x),
            top: Math.min(selection.startY, y),
            right: Math.max(selection.startX, x),
            bottom: Math.max(selection.startY, y)
        };
        setSelectedIds(findIntersectingParagraphs(selRect));
    }, [isDragging, selection, findIntersectingParagraphs]);

    const handleMouseUp = useCallback(async () => {
        if (isDragging && selection) {
            if (selectedIds.size > 0) {
                await copySelectedToClipboard(selectedIds);
            }
            if (onRectCrop && containerRef.current) {
                const selLeft = Math.min(selection.startX, selection.currentX);
                const selTop = Math.min(selection.startY, selection.currentY);
                const selRight = Math.max(selection.startX, selection.currentX);
                const selBottom = Math.max(selection.startY, selection.currentY);
                if (Math.abs(selRight - selLeft) > 5 && Math.abs(selBottom - selTop) > 5) {
                    // const pageDiv = containerRef.current.parentElement;
                    const pageDiv = false; // ANCHOR paragraph overlay image crop
                    if (pageDiv) {
                        const cropped = captureCanvasCrop(pageDiv, selLeft, selTop, selRight, selBottom);
                        if (cropped) {
                            const selectedContent = paragraphBoundsRef.current
                                .filter(b => selectedIds.has(b.kgnf_id))
                                .sort((a, b) => a.top - b.top)
                                .map(b => b.content)
                                .join("\n");
                            const cropData: RectCropData = { base64: cropped, page: pageNumber + 1, selectedText: selectedContent };
                            //console.log(`‹rect_crop› page=${pageNumber + 1} size=${Math.round(cropped.length / 1024)}KB`);
                            //console.log(cropped);
                            onRectCrop(cropData);
                        }
                    }
                }
            }
        }
        setIsDragging(false);
        setSelection(null);
        setTimeout(() => setSelectedIds(new Set()), 500);
    }, [isDragging, selection, selectedIds, copySelectedToClipboard, onRectCrop, pageNumber]);

    const handleClick = useCallback((e: React.MouseEvent, paragraph: gautoparagraph) => {
        e.stopPropagation();
        setClickedId(paragraph.kgnf_id);

        // Reset animation after it completes
        setTimeout(() => setClickedId(null), 300);

        onParagraphClick?.(paragraph);
    }, [onParagraphClick]);


    if (!visible || !pageParagraphs || pageParagraphs.length === 0) return null;

    const selRect = getSelectionRect();

    return (
        <div
            ref={containerRef}
            className={styles.overlayContainer}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
        >
            {/* Selection rectangle */}
            {isDragging && selRect && (
                <div
                    className={styles.selectionRect}
                    style={{
                        left: selRect.left,
                        top: selRect.top,
                        width: selRect.right - selRect.left,
                        height: selRect.bottom - selRect.top
                    }}
                />
            )}

            {paragraphBounds.map((bounds, idx) => {
                const isClicked = clickedId === bounds.kgnf_id;
                const isSelected = selectedIds.has(bounds.kgnf_id);
                const paragraph = pageParagraphs.find(p => p.kgnf_id === bounds.kgnf_id);

                return (
                    <div
                        key={`para-${bounds.kgnf_id}-${idx}`}
                        className={`${styles.paragraphBox} ${isClicked ? styles.clicked : ""} ${isSelected ? styles.selected : ""}`}
                        style={{
                            position: "absolute",
                            left: `${bounds.left}px`,
                            top: `${bounds.top}px`,
                            width: `${bounds.right - bounds.left}px`,
                            height: `${bounds.bottom - bounds.top}px`
                        }}
                        onClick={(e) => paragraph && handleClick(e, paragraph)}
                        title={bounds.content}
                        data-kgnf-id={bounds.kgnf_id}
                    />
                );
            })}
        </div>
    );
};
